import type { Backend } from "@superego/backend";
import {
  extractErrorDetails,
  makeUnsuccessfulResult,
} from "@superego/shared-utils";
import * as v from "valibot";
import BackgroundJobExecutor from "./BackgroundJobExecutor.js";
import type Config from "./Config.js";
import LiveConversationStore from "./LiveConversationStore.js";
import makeResultError from "./makers/makeResultError.js";
import makeValidationIssues from "./makers/makeValidationIssues.js";
import type DataRepositories from "./requirements/DataRepositories.js";
import type DataRepositoriesManager from "./requirements/DataRepositoriesManager.js";
import type InferenceServiceFactory from "./requirements/InferenceServiceFactory.js";
import type JavascriptSandbox from "./requirements/JavascriptSandbox.js";
import type TypescriptCompiler from "./requirements/TypescriptCompiler.js";
import AppsCreate from "./usecases/apps/Create.js";
import AppsCreateNewVersion from "./usecases/apps/CreateNewVersion.js";
import AppsDelete from "./usecases/apps/Delete.js";
import AppsGetState from "./usecases/apps/GetState.js";
import AppsList from "./usecases/apps/List.js";
import AppsUpdateName from "./usecases/apps/UpdateName.js";
import AppsUpdatePermissions from "./usecases/apps/UpdatePermissions.js";
import AppsUpdateState from "./usecases/apps/UpdateState.js";
import AssistantsContinueConversation from "./usecases/assistants/ContinueConversation.js";
import AssistantsDeleteConversation from "./usecases/assistants/DeleteConversation.js";
import AssistantsGetConversation from "./usecases/assistants/GetConversation.js";
import AssistantsGetDeveloperPrompts from "./usecases/assistants/GetDeveloperPrompts.js";
import AssistantsGetLiveConversation from "./usecases/assistants/GetLiveConversation.js";
import AssistantsListConversations from "./usecases/assistants/ListConversations.js";
import AssistantsRecoverConversation from "./usecases/assistants/RecoverConversation.js";
import AssistantsRetryLastResponse from "./usecases/assistants/RetryLastResponse.js";
import AssistantsSearchConversations from "./usecases/assistants/SearchConversations.js";
import AssistantsStartConversation from "./usecases/assistants/StartConversation.js";
import BackgroundJobsGet from "./usecases/background-jobs/Get.js";
import BackgroundJobsList from "./usecases/background-jobs/List.js";
import BoutiqueGetPack from "./usecases/boutique/GetPack.js";
import BoutiqueListPacks from "./usecases/boutique/ListPacks.js";
import CollectionCategoriesCreate from "./usecases/collection-categories/Create.js";
import CollectionCategoriesDelete from "./usecases/collection-categories/Delete.js";
import CollectionCategoriesList from "./usecases/collection-categories/List.js";
import CollectionCategoriesUpdate from "./usecases/collection-categories/Update.js";
import CollectionsCreate from "./usecases/collections/Create.js";
import CollectionsCreateMany from "./usecases/collections/CreateMany.js";
import CollectionsCreateNewVersion from "./usecases/collections/CreateNewVersion.js";
import CollectionsDelete from "./usecases/collections/Delete.js";
import CollectionsGet from "./usecases/collections/Get.js";
import CollectionsGetTypescriptSchema from "./usecases/collections/GetTypescriptSchema.js";
import CollectionsGetVersion from "./usecases/collections/GetVersion.js";
import CollectionsList from "./usecases/collections/List.js";
import CollectionUpdateLatestVersionSettings from "./usecases/collections/UpdateLatestVersionSettings.js";
import CollectionsUpdateSettings from "./usecases/collections/UpdateSettings.js";
import DatabaseExport from "./usecases/database/Export.js";
import DocumentsCreate from "./usecases/documents/Create.js";
import DocumentsCreateMany from "./usecases/documents/CreateMany.js";
import DocumentsCreateNewVersion from "./usecases/documents/CreateNewVersion.js";
import DocumentsDelete from "./usecases/documents/Delete.js";
import DocumentsExecuteTypescriptFunction from "./usecases/documents/ExecuteTypescriptFunction.js";
import DocumentsGet from "./usecases/documents/Get.js";
import DocumentsGetVersion from "./usecases/documents/GetVersion.js";
import DocumentsList from "./usecases/documents/List.js";
import DocumentsListVersions from "./usecases/documents/ListVersions.js";
import DocumentsSearch from "./usecases/documents/Search.js";
import FilesGetContent from "./usecases/files/GetContent.js";
import GlobalSettingsGet from "./usecases/global-settings/Get.js";
import GlobalSettingsUpdate from "./usecases/global-settings/Update.js";
import InferenceImplementTypescriptModule from "./usecases/inference/ImplementTypescriptModule.js";
import InferenceStt from "./usecases/inference/Stt.js";
import PacksInstall from "./usecases/packs/Install.js";
import type BackendUsecase from "./utils/BackendUsecase.js";

export default class ExecutingBackend implements Backend {
  collectionCategories: Backend["collectionCategories"];
  collections: Backend["collections"];
  documents: Backend["documents"];
  files: Backend["files"];
  assistants: Backend["assistants"];
  inference: Backend["inference"];
  apps: Backend["apps"];
  packs: Backend["packs"];
  boutique: Backend["boutique"];
  backgroundJobs: Backend["backgroundJobs"];
  globalSettings: Backend["globalSettings"];
  database: Backend["database"];

  private liveConversationStore: LiveConversationStore;
  private backgroundJobExecutor: BackgroundJobExecutor;

  private resolvedConfig: Config;

  constructor(
    private dataRepositoriesManager: DataRepositoriesManager,
    private javascriptSandbox: JavascriptSandbox,
    private typescriptCompiler: TypescriptCompiler,
    private inferenceServiceFactory: InferenceServiceFactory,
    config?: Partial<Config>,
  ) {
    this.resolvedConfig = {
      conversationProcessingStuckTimeout: 5 * 60 * 1_000,
      backgroundJobProcessingStuckTimeout: 5 * 60 * 1_000,
      ...config,
    };
    this.collectionCategories = {
      create: this.makeUsecase(CollectionCategoriesCreate, true),
      update: this.makeUsecase(CollectionCategoriesUpdate, true),
      delete: this.makeUsecase(CollectionCategoriesDelete, true),
      list: this.makeUsecase(CollectionCategoriesList, false),
    };

    this.collections = {
      create: this.makeUsecase(CollectionsCreate, true),
      createMany: this.makeUsecase(CollectionsCreateMany, true),
      updateSettings: this.makeUsecase(CollectionsUpdateSettings, true),
      createNewVersion: this.makeUsecase(CollectionsCreateNewVersion, true),
      updateLatestVersionSettings: this.makeUsecase(
        CollectionUpdateLatestVersionSettings,
        true,
      ),
      delete: this.makeUsecase(CollectionsDelete, true),
      list: this.makeUsecase(CollectionsList, false),
      get: this.makeUsecase(CollectionsGet, false),
      getVersion: this.makeUsecase(CollectionsGetVersion, false),
      getTypescriptSchema: this.makeUsecase(
        CollectionsGetTypescriptSchema,
        false,
      ),
    };

    this.documents = {
      create: this.makeUsecase(DocumentsCreate, true),
      createMany: this.makeUsecase(DocumentsCreateMany, true),
      createNewVersion: this.makeUsecase(DocumentsCreateNewVersion, true),
      delete: this.makeUsecase(DocumentsDelete, true),
      list: this.makeUsecase(DocumentsList, false),
      listVersions: this.makeUsecase(DocumentsListVersions, false),
      search: this.makeUsecase(DocumentsSearch, false),
      get: this.makeUsecase(DocumentsGet, false),
      getVersion: this.makeUsecase(DocumentsGetVersion, false),
      executeTypescriptFunction: this.makeUsecase(
        DocumentsExecuteTypescriptFunction,
        false,
      ),
    };

    this.files = {
      getContent: this.makeUsecase(FilesGetContent, false),
    };

    this.assistants = {
      startConversation: this.makeUsecase(AssistantsStartConversation, true),
      continueConversation: this.makeUsecase(
        AssistantsContinueConversation,
        true,
      ),
      retryLastResponse: this.makeUsecase(AssistantsRetryLastResponse, true),
      recoverConversation: this.makeUsecase(
        AssistantsRecoverConversation,
        true,
      ),
      deleteConversation: this.makeUsecase(AssistantsDeleteConversation, true),
      listConversations: this.makeUsecase(AssistantsListConversations, false),
      getConversation: this.makeUsecase(AssistantsGetConversation, false),
      searchConversations: this.makeUsecase(
        AssistantsSearchConversations,
        false,
      ),
      getLiveConversation: this.makeUsecase(
        AssistantsGetLiveConversation,
        false,
      ),
      getDeveloperPrompts: this.makeUsecase(
        AssistantsGetDeveloperPrompts,
        false,
      ),
    };

    this.inference = {
      stt: this.makeUsecase(InferenceStt, false),
      implementTypescriptModule: this.makeUsecase(
        InferenceImplementTypescriptModule,
        false,
      ),
    };

    this.apps = {
      getState: this.makeUsecase(AppsGetState, false),
      updateState: this.makeUsecase(AppsUpdateState, false),
      create: this.makeUsecase(AppsCreate, true),
      updateName: this.makeUsecase(AppsUpdateName, true),
      updatePermissions: this.makeUsecase(AppsUpdatePermissions, true),
      createNewVersion: this.makeUsecase(AppsCreateNewVersion, true),
      delete: this.makeUsecase(AppsDelete, true),
      list: this.makeUsecase(AppsList, false),
    };

    this.packs = {
      install: this.makeUsecase(PacksInstall, true),
    };

    this.boutique = {
      listPacks: this.makeUsecase(BoutiqueListPacks, false),
      getPack: this.makeUsecase(BoutiqueGetPack, false),
    };

    this.backgroundJobs = {
      list: this.makeUsecase(BackgroundJobsList, false),
      get: this.makeUsecase(BackgroundJobsGet, false),
    };

    this.globalSettings = {
      get: this.makeUsecase(GlobalSettingsGet, false),
      update: this.makeUsecase(GlobalSettingsUpdate, true),
    };

    this.database = {
      export: this.makeUsecase(DatabaseExport, false),
    };

    this.liveConversationStore = new LiveConversationStore();
    this.backgroundJobExecutor = new BackgroundJobExecutor(
      dataRepositoriesManager,
      javascriptSandbox,
      typescriptCompiler,
      inferenceServiceFactory,
      this.liveConversationStore,
      this.resolvedConfig,
    );
  }

  private makeUsecase<Exec extends (...args: any[]) => any>(
    UsecaseClass: new (
      repos: DataRepositories,
      javascriptSandbox: JavascriptSandbox,
      typescriptCompiler: TypescriptCompiler,
      inferenceServiceFactory: InferenceServiceFactory,
      liveConversationStore: LiveConversationStore,
      config: Config,
    ) => BackendUsecase<Exec>,
    triggerBackgroundJobCheck: boolean,
  ): Exec {
    return (async (...args: any[]) =>
      this.dataRepositoriesManager
        .runInSerializableTransaction<Awaited<ReturnType<Exec>>>(
          async (repos) => {
            const usecase = new UsecaseClass(
              repos,
              this.javascriptSandbox,
              this.typescriptCompiler,
              this.inferenceServiceFactory,
              this.liveConversationStore,
              this.resolvedConfig,
            );
            const argumentsValidationResult = v.safeParse(
              usecase.argumentsSchema,
              args,
            );
            if (!argumentsValidationResult.success) {
              return {
                action: "rollback",
                returnValue: makeUnsuccessfulResult(
                  makeResultError("ArgumentsNotValid", {
                    issues: makeValidationIssues(
                      argumentsValidationResult.issues,
                    ),
                  }),
                ) as Awaited<ReturnType<Exec>>,
              };
            }
            const result = await usecase.exec(
              ...argumentsValidationResult.output,
            );
            const resultValidationResult = v.safeParse(
              usecase.resultSchema,
              result,
            );
            if (!resultValidationResult.success) {
              return {
                action: "rollback",
                returnValue: makeUnsuccessfulResult(
                  makeResultError("UnexpectedError", {
                    cause: {
                      reason: "ResultValidationFailed",
                      issues: makeValidationIssues(
                        resultValidationResult.issues,
                      ),
                    },
                  }),
                ) as Awaited<ReturnType<Exec>>,
              };
            }
            return {
              action: result.success ? "commit" : "rollback",
              returnValue: result as Awaited<ReturnType<Exec>>,
            };
          },
        )
        .then((result) => {
          // We trigger a background job check only _after_ the transaction that
          // might have created some background jobs has been committed. (Else
          // the BackgroundJobExecutor wouldn't even see the created background
          // jobs, since it executes in a separate transaction.)
          if (triggerBackgroundJobCheck) {
            // Trigger after the current microtask queue drains.
            setTimeout(() => {
              this.backgroundJobExecutor.executeNext().catch((error) => {
                console.error("Error triggering next background job execution");
                console.error(error);
              });
            }, 0);
          }
          return result;
        })
        .catch((error) =>
          makeUnsuccessfulResult(
            makeResultError("UnexpectedError", {
              cause: extractErrorDetails(error),
            }),
          ),
        )) as Exec;
  }
}
